/**
 * Full use-case page content — pain/transform, workflows, screenshots, SEO.
 * Screenshots: bundled PNGs (same as platform walkthrough).
 */
import homePng from '../../assets/screenshots/home.png';
import sync2Png from '../../assets/screenshots/sync2.png';
import sync3Png from '../../assets/screenshots/sync3.png';
import sync5Png from '../../assets/screenshots/sync5.png';

const TRUST_DEFAULT = ['Most setups under 5 minutes', 'Usually under 8 clicks', 'No engineering required'];

function blocks(painBefore, painAfter, workflows, screenshots, autoUpdates, destinationsLead, whyTeams, relatedSlugs, finalCta, extras = {}) {
    return {
        painBefore,
        painAfter,
        workflows,
        screenshots,
        autoUpdates,
        destinationsLead,
        whyTeams,
        relatedSlugs,
        finalCta,
        trustLine: TRUST_DEFAULT,
        ...extras,
    };
}

/** @type {Record<string, ReturnType<typeof blocks> & { title: string, metaDescription: string, hero: object }>} */
export const USE_CASE_PAGE_CONFIG = {
    'stop-exporting-data': {
        title: 'Stop Exporting CSVs—Keep Data Updated Automatically | Data Connector',
        metaDescription:
            'Stop rebuilding spreadsheets every week. Replace manual CSV exports with connections that refresh orders, campaigns, and CRM data automatically.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'End the weekly export loop your team runs by hand.',
            headlineAccent: '',
            lead: 'Same spreadsheets and dashboards—now on a schedule instead of a calendar reminder to download CSVs.',
            workflowPills: ['Shopify → QuickBooks', 'Salesforce → Google Sheets', 'HubSpot → Power BI'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Export CSVs manually every week',
                'Rebuild the same spreadsheet layout again and again',
                'Wonder whether the numbers are still current',
            ],
            [
                'Reports and destinations update on a schedule you choose',
                'The same Sheet or workbook stays current—no re-import',
                'Dashboards reflect today—without “did I export yesterday?” doubt',
            ],
            [
                { source: 'Shopify', dest: 'QuickBooks', outcome: 'Orders, refunds, and payouts flow without CSV bridges.' },
                { source: 'Meta Ads', dest: 'Google Sheets', outcome: 'Campaign reporting without manual exports.' },
                { source: 'Stripe', dest: 'PostgreSQL', outcome: 'Financial data ready for reporting and BI.' },
                { source: 'Salesforce', dest: 'Excel', outcome: 'Keep shared spreadsheets updated automatically.' },
                { source: 'HubSpot', dest: 'Power BI', outcome: 'CRM and pipeline views stay fresh for leadership.' },
            ],
            [
                { src: homePng, alt: 'Dashboard showing active syncs and next run times.', caption: 'See every connection and when it last ran.' },
                { src: sync2Png, alt: 'Selecting tables and fields to sync.', caption: 'Pick exactly what should stay updated—no dump of everything.' },
            ],
            ['Orders', 'Campaign rows', 'CRM records', 'Payments & payouts', 'Inventory lines', 'Spreadsheets', 'KPI tabs'],
            'Send updates to Google Sheets, Excel, Power BI, databases, and other tools your team already opens every day.',
            [
                {
                    title: 'Quit the "export_download_final.csv" loop',
                    description: 'The same workbook path gets fresh rows—no more attachment tennis across Slack.',
                },
                {
                    title: 'Stop presenting a tab nobody updated',
                    description: 'Fewer manual hops means fewer wrong slices someone calls "the source of truth."',
                },
                {
                    title: 'Put connections live without an IT ticket',
                    description: 'Ops or finance can own setup—authorize sources and destinations in minutes.',
                },
                {
                    title: 'Point everyone at one refreshed dataset',
                    description: 'No more five competing exports from the same morning.',
                },
            ],
            ['weekly-report-automation', 'automate-spreadsheets', 'automated-reporting', 'sync-data-between-apps'],
            {
                headline: 'Ready to stop rebuilding spreadsheets every week?',
                sub: 'Connect your tools once—your reports update automatically after that.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Last sync', value: '6 min ago' },
                        { label: 'Status', value: 'Healthy' },
                        { label: 'Rows this run', value: '18,204' },
                    ],
                    bullets: [
                        'See every connection, last run, and health from one dashboard.',
                        'Choose exactly which objects and fields sync—skip full-database dumps.',
                        'Drop rows into Sheets, Excel, BI, or a database your analysts already use.',
                    ],
                },
                visualCompare: {
                    before: [
                        'CSV exports on a calendar reminder',
                        'Spreadsheets named like FINAL_final_v9.xlsx',
                        'Rebuild pivots when column names change',
                        'Ask "is this still accurate?" in every meeting',
                    ],
                    after: [
                        'Jobs run on the schedule you choose',
                        'The same Sheet path or table stays current',
                        'Status reads Healthy with a timestamp you can defend',
                        'Dashboards show today without another export pass',
                    ],
                },
                pullQuote:
                    'Monday starts with live rows—not another export loop your team fact-checks by hand.',
            },
        ),
    },

    'automated-reporting': {
        title: 'Automated Reporting—Dashboards & Sheets That Stay Current | Data Connector',
        metaDescription:
            'Automate weekly and leadership reporting. Keep Google Sheets, Excel, and BI dashboards updated from Shopify, Salesforce, HubSpot, and more.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Monday leadership packs hydrate themselves—not your inbox.',
            headlineAccent: '',
            lead: 'Board decks and KPI tabs pull from live tools—refreshed before standups, not rebuilt from static exports.',
            workflowPills: ['QuickBooks → Sheets', 'Salesforce → Excel', 'Facebook Ads → Looker Studio'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Most setups take under five minutes. No engineers required.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Pull static snapshots into decks every Monday',
                'Email CSV attachments around the team',
                'Spend meetings debating which file is “the real one”',
            ],
            [
                'One schedule keeps Sheets and BI sources aligned',
                'Same metrics—always tied to today’s data',
                'Fewer “can you resend the numbers?” loops',
            ],
            [
                { source: 'HubSpot', dest: 'Power BI', outcome: 'Pipeline and campaign metrics stay board-ready.' },
                { source: 'Shopify', dest: 'Google Sheets', outcome: 'Store performance without manual pulls.' },
                { source: 'LinkedIn Ads', dest: 'Looker Studio', outcome: 'Marketing dashboards stay client-ready.' },
                { source: 'Stripe', dest: 'Excel', outcome: 'Finance workbooks stay tied to real transactions.' },
            ],
            [
                { src: homePng, alt: 'Sync dashboard with healthy jobs and activity.', caption: 'Monitor every automated report from one place.' },
                { src: sync5Png, alt: 'Review and start sync schedule.', caption: 'Choose how often reporting refreshes—then let it run.' },
            ],
            ['Revenue totals', 'Pipeline stages', 'Campaign spend', 'Client KPIs', 'Weekly leadership views', 'Spreadsheet tabs', 'Dashboard tiles'],
            'Push updates into Sheets, Excel, Power BI, Looker Studio, and databases—where your stakeholders already look.',
            [
                {
                    title: 'Walk into Monday with numbers already refreshed',
                    description: 'Leadership prep shrinks when tabs hydrate overnight—not during your coffee.',
                },
                {
                    title: 'Cut paste errors that quietly break formulas',
                    description: 'One scheduled connection replaces brittle CSV bridges between tools.',
                },
                {
                    title: 'Keep board decks tied to live rows',
                    description: 'Same metrics—always stamped with a recent sync, not Friday\'s export.',
                },
                {
                    title: 'Stop waiting on BI for every new cut',
                    description: 'Add a field or filter without filing work for a net-new pipeline.',
                },
            ],
            ['weekly-report-automation', 'client-reporting', 'marketing-dashboard', 'stop-exporting-data'],
            {
                headline: 'Ready for reporting that runs without you?',
                sub: 'Connect once—then leadership and client views stay current on autopilot.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Auto-refresh', value: 'Enabled' },
                        { label: 'Last successful sync', value: '8:14 AM' },
                        { label: 'Records synced', value: '42,891' },
                    ],
                    bullets: [
                        'Watch jobs so automated reports finish before your standup.',
                        'Schedule hourly, daily, or weekly refreshes—your ritual, not ours.',
                        'Push into Sheets, Excel, or BI where stakeholders already look.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Paste static screenshots into decks every Monday',
                        'Email CSVs labeled "use_this_one"',
                        'Spend standups arguing which file is real',
                        'Rebuild charts when someone asks for "last week again"',
                    ],
                    after: [
                        'Connections refresh before your meeting block',
                        'One workbook or semantic model stays wired to live apps',
                        'Everyone opens the same refreshed artifact',
                        'Filters apply to current rows—not Friday\'s export',
                    ],
                },
                pullQuote:
                    'Spend the meeting on what changed—not on whether the spreadsheet is still honest.',
            },
        ),
    },

    'client-reporting': {
        title: 'Client Reporting Automation—Always-Current Client Dashboards | Data Connector',
        metaDescription:
            'Keep client reports and dashboards updated automatically. Stop Friday rebuilds—sync ads, CRM, and sales data into Sheets, Looker Studio, and Power BI.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Stop rebuilding the same client reports every Friday.',
            headlineAccent: '',
            lead: 'Your client dashboards stay current without the CSV scramble—live before check-ins, not panic-refreshed the night before.',
            workflowPills: ['Meta Ads → Looker Studio', 'HubSpot → Sheets', 'Google Ads → Power BI'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Built for agencies and consultancies. No engineers required.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'screenshot numbers Thursday night',
                'Rebuild the same deck layout every week',
                'Answer “is this still accurate?” in every meeting',
            ],
            [
                'Shared reports reflect current spend and leads',
                'Same template—always filled with fresh rows',
                'Clients see one trustworthy link instead of five attachments',
            ],
            [
                { source: 'Facebook Ads', dest: 'Looker Studio', outcome: 'Campaign pacing clients can trust.' },
                { source: 'HubSpot', dest: 'Google Sheets', outcome: 'Pipeline rows ready for account reviews.' },
                { source: 'Shopify', dest: 'Power BI', outcome: 'Store KPIs for QBR packs without re-querying.' },
            ],
            [
                { src: homePng, alt: 'Operational dashboard for sync health.', caption: 'Prove everything ran before the client call.' },
                { src: sync3Png, alt: 'Choosing a destination for synced data.', caption: 'Drop results exactly where clients already work.' },
            ],
            ['Ad spend', 'Lead volume', 'CRM stages', 'ROAS', 'Client KPI tabs', 'Weekly scorecards', 'Budget pacing'],
            'Deliver updates into Looker Studio, Sheets, Excel, and Power BI—the tools clients already recognize.',
            [
                {
                    title: 'Clients stop asking for "the latest version"',
                    description: 'Shared links stay credible—no more "ignore the attachment I sent earlier."',
                },
                {
                    title: 'Quit screenshotting numbers Thursday night',
                    description: 'Walk into QBRs knowing the dashboard already ran this morning.',
                },
                {
                    title: 'Replace surprise CSVs with one trustworthy link',
                    description: 'Looks intentional—your team is not guessing which file is current.',
                },
                {
                    title: 'Spend the call on insights—not rebuilding tabs',
                    description: 'Less copy/paste hygiene means more time on what actually moved spend or leads.',
                },
            ],
            ['automated-reporting', 'marketing-dashboard', 'weekly-report-automation', 'crm-to-spreadsheet'],
            {
                headline: 'Ready to retire the Friday report scramble?',
                sub: 'Connect client sources once—shared dashboards and Sheets stay fresh all week.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Client dashboards', value: '32 active' },
                        { label: 'Updated', value: '11 min ago' },
                        { label: 'Status', value: 'Healthy' },
                    ],
                    bullets: [
                        'Prove syncs finished before the client call—not during it.',
                        'Route paid + CRM rows into Looker Studio, Sheets, or Power BI.',
                        'Keep the same template; swap in live performance automatically.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Panic-refresh tabs the night before a QBR',
                        'Email decks with "final_FINAL" filenames',
                        'Answer "is this still accurate?" from every stakeholder',
                        'Rebuild the same eight slides when spend shifts',
                    ],
                    after: [
                        'One dashboard link your client keeps bookmarked',
                        'Rows refresh before standing meetings',
                        'Show last sync time instead of apologizing for screenshots',
                        'Spend reviews on pacing—not paste errors',
                    ],
                },
                pullQuote:
                    'Your client checks one link all week—not five attachments that disagree with each other.',
            },
        ),
    },

    'marketing-dashboard': {
        title: 'Marketing Dashboard Automation—Ads & Campaign Data in One Place | Data Connector',
        metaDescription:
            'Automate marketing dashboards: sync Meta, Google Ads, LinkedIn, and CRM metrics into Sheets, Looker Studio, and Power BI without weekly exports.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Stop stitching six ad exports before every review.',
            headlineAccent: '',
            lead: 'Paid, organic, and CRM rows land in one place—the tiles your team actually opens daily.',
            workflowPills: ['Google Ads → Sheets', 'Meta Ads → Looker Studio', 'HubSpot → Power BI'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Most marketing teams go live in one sitting.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Open six ad platforms to build one slide',
                'Merge CSVs with different column names',
                'Lose half a day when someone asks for “last week again”',
            ],
            [
                'Campaign and spend rows land in one workbook or BI model',
                'Filters apply to live data—not Friday’s export',
                'Standups start from the same refreshed tile',
            ],
            [
                { source: 'Google Ads', dest: 'Google Sheets', outcome: 'Search and PMax metrics without manual pulls.' },
                { source: 'Meta Ads', dest: 'Looker Studio', outcome: 'Cross-channel pacing in one client-facing view.' },
                { source: 'LinkedIn Ads', dest: 'Power BI', outcome: 'B2B funnel metrics beside CRM outcomes.' },
            ],
            [
                { src: homePng, alt: 'Monitoring sync jobs for marketing data.', caption: 'Know every feed succeeded before you brief leadership.' },
                { src: sync2Png, alt: 'Choosing marketing objects to sync.', caption: 'Include only the objects your dashboard needs.' },
            ],
            ['Campaign spend', 'Impressions', 'Conversions', 'CRM lifecycle stages', 'Creative IDs', 'GEO splits', 'Budget pacing'],
            'Land marketing metrics in Sheets, Looker Studio, Power BI, Excel, and databases for modeling.',
            [
                {
                    title: 'Start standups from live spend—not Friday\'s export',
                    description: 'Growth and finance read the same refreshed rows in the first five minutes.',
                },
                {
                    title: 'Stop merging six ad platforms by hand',
                    description: 'Automate the boring joins so you talk about strategy, not VLOOKUPs.',
                },
                {
                    title: 'Give BI a table that does not change names weekly',
                    description: 'Analysts connect to stable sheets or warehouse tables instead of mystery files.',
                },
                {
                    title: 'Answer "last week again" without a half-day detour',
                    description: 'Filters sit on current data—no emergency re-pull from every ad account.',
                },
            ],
            ['automated-reporting', 'client-reporting', 'weekly-report-automation', 'stop-exporting-data'],
            {
                headline: 'Want marketing dashboards without the CSV shuffle?',
                sub: 'Wire your ad and CRM sources once—your dashboard tiles stay current after that.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Campaign rows', value: 'Syncing' },
                        { label: 'Last sync', value: '4 min ago' },
                        { label: 'Status', value: 'Healthy' },
                    ],
                    bullets: [
                        'Know every feed hit before you brief leadership or a client.',
                        'Sync only the marketing objects your model needs—skip noise.',
                        'Land in Sheets, Looker Studio, or Power BI where people already look daily.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Open every ad account to export "just one more" CSV',
                        'Manually align column names that never match',
                        'Lose half a day on "can I get last week again?"',
                        'Present tiles tied to Friday night data',
                    ],
                    after: [
                        'One workbook or model ingests every channel you wire up',
                        'Column mapping happens once—then it keeps working',
                        'Refreshes run on a schedule you can defend in a meeting',
                        'Standups start from today\'s totals—not stale pulls',
                    ],
                },
                pullQuote:
                    'Your dashboard tells one story—because the rows underneath actually agree.',
            },
        ),
    },

    'weekly-report-automation': {
        title: 'Weekly Report Automation—Same Report, Always Fresh Numbers | Data Connector',
        metaDescription:
            'Automate weekly reports: keep the same spreadsheet or dashboard layout filled with current numbers from Shopify, Salesforce, Stripe, and ads platforms.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Monday\'s pack fills itself—same template, live numbers.',
            headlineAccent: '',
            lead: 'Keep tabs and charts—swap in rows on the day and time your ritual expects.',
            workflowPills: ['Stripe → Sheets', 'Salesforce → Excel', 'QuickBooks → Power BI'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Schedule daily, weekly, or hourly—whatever matches your ritual.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Rebuild the same workbook tabs every Monday',
                'Paste over last week’s numbers by hand',
                'Hope nobody edited the wrong column',
            ],
            [
                'Rows refresh on your cadence—weekly, daily, or faster',
                'Formulas keep working against live inputs',
                'Everyone opens the same trusted file',
            ],
            [
                { source: 'QuickBooks', dest: 'Google Sheets', outcome: 'P&L lines for the leadership pack.' },
                { source: 'Shopify', dest: 'Excel', outcome: 'Store KPIs for the ops review.' },
                { source: 'HubSpot', dest: 'Sheets', outcome: 'Pipeline snapshot for the revenue meeting.' },
            ],
            [
                { src: sync5Png, alt: 'Schedule and review before starting sync.', caption: 'Pick weekly timing that matches your Monday standup.' },
                { src: homePng, alt: 'Dashboard of automated weekly jobs.', caption: 'See each weekly job at a glance.' },
            ],
            ['Weekly totals', 'Pipeline counts', 'Spend summaries', 'Inventory snapshots', 'Cash positions', 'KPI tabs'],
            'Feed weekly packs into Sheets, Excel, Power BI, and Looker Studio—where leadership already looks.',
            [
                {
                    title: 'Open the same workbook every Monday—already filled',
                    description: 'Numbers are waiting in the tabs you trust—not chased across inboxes.',
                },
                {
                    title: 'Paste less in tabs where a typo breaks finance',
                    description: 'Fewer manual bridges into sensitive revenue or inventory sheets.',
                },
                {
                    title: 'Keep your template—swap in live values',
                    description: 'Automation overwrites the inputs your charts already reference.',
                },
                {
                    title: 'Stop emailing three versions of "weekly_actuals"',
                    description: 'Everyone references one path that refreshed overnight.',
                },
            ],
            ['automated-reporting', 'automate-spreadsheets', 'stop-exporting-data', 'marketing-dashboard'],
            {
                headline: 'Done rebuilding the same weekly pack?',
                sub: 'Connect your sources once—the ritual keeps running without the manual rebuild.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Schedule', value: 'Every Mon 6 AM' },
                        { label: 'Next run', value: 'in 2d 14h' },
                        { label: 'Last run', value: 'Success' },
                    ],
                    bullets: [
                        'Pick weekly timing that matches your standup or close calendar.',
                        'See each recurring job and whether it finished cleanly.',
                        'Keep formulas pointed at tables that refresh—not static imports.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Rebuild the same tabs every Monday morning',
                        'Paste over last week and pray nobody shifted a column',
                        'Email "final_weekly_v4" while someone edits v3',
                        'Hope the deck matches the spreadsheet',
                    ],
                    after: [
                        'Rows land before your first meeting',
                        'Named ranges keep charts wired to structured refreshes',
                        'Everyone opens the same file path or BI bookmark',
                        'You defend a schedule—not a chain of manual steps',
                    ],
                },
                pullQuote:
                    'The ritual stays—your team stops being the human cron job that rebuilds it.',
            },
        ),
    },

    'sync-data-between-apps': {
        title: 'Sync Data Between Apps—Keep CRM, Sheets & Ops Aligned | Data Connector',
        metaDescription:
            'Keep Shopify, Salesforce, HubSpot, and accounting tools aligned automatically. No CSV bridges—scheduled syncs your team can manage without engineering.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Systems stay aligned—without CSVs flying between teams.',
            headlineAccent: '',
            lead: 'Orders, customers, and money facts land where each team owns the next step—scheduled, not emailed.',
            workflowPills: ['Shopify → QuickBooks', 'Salesforce → PostgreSQL', 'Stripe → Sheets'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Most teams connect two systems on day one and expand from there.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Email CSVs between departments',
                'Manually update the “system of record” twice',
                'Patch gaps with off-books spreadsheets',
            ],
            [
                'Each system receives updates automatically',
                'Ops and finance share one timeline of truth',
                'Fewer “which file is authoritative?” debates',
            ],
            [
                { source: 'Shopify', dest: 'QuickBooks', outcome: 'Orders and payouts stay reconciled.' },
                { source: 'Salesforce', dest: 'PostgreSQL', outcome: 'CRM facts ready for analytics sandboxes.' },
                { source: 'Stripe', dest: 'Google Sheets', outcome: 'Cash movement visible for FP&A.' },
            ],
            [
                { src: homePng, alt: 'Overview of connections between business apps.', caption: 'Watch health across every bridge you rely on.' },
                { src: sync3Png, alt: 'Routing data to the right destination.', caption: 'Send each dataset to the system that owns the next step.' },
            ],
            ['Orders', 'Customers', 'Invoices', 'Payments', 'Subscriptions', 'Inventory adjustments', 'Lead stages'],
            'Route into accounting, warehouses, Sheets, Power BI, and custom databases—wherever the next workflow lives.',
            [
                {
                    title: 'Stop typing the same order into two systems',
                    description: 'Information lands once—then each app stays aligned on your cadence.',
                },
                {
                    title: 'Give auditors a trail that lives in tools—not attachments',
                    description: 'Sync history beats "forward me the CSV from last Tuesday."',
                },
                {
                    title: 'Shrink month-end matching marathons',
                    description: 'Cash and orders converge nightly instead of in a crisis weekend.',
                },
                {
                    title: 'Change a field without waiting on a sprint',
                    description: 'Ops adjusts objects or columns when the business shifts—no rewrite project.',
                },
            ],
            ['stop-exporting-data', 'orders-to-accounting', 'crm-to-spreadsheet', 'automate-spreadsheets'],
            {
                headline: 'Ready to retire CSV bridges between teams?',
                sub: 'Connect the apps once—updates keep flowing on your schedule.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Bridges active', value: '6' },
                        { label: 'Last sync', value: '12 min ago' },
                        { label: 'Errors', value: '0' },
                    ],
                    bullets: [
                        'See health across every connection finance and ops rely on.',
                        'Route each dataset to the system that owns the next step.',
                        'Replace inbox CSV tennis with scheduled, repeatable syncs.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Email CSVs between departments',
                        'Update the "system of record" twice by hand',
                        'Patch gaps with shadow spreadsheets',
                        'Argue which export is authoritative',
                    ],
                    after: [
                        'Each system receives rows on a schedule you trust',
                        'Ops and finance share one timeline of updates',
                        'Changes trace to connections—not mystery attachments',
                        'Bridge gaps without hiring integrations headcount',
                    ],
                },
                pullQuote:
                    'Your apps stay cousins—not strangers who only talk when someone emails a file.',
            },
        ),
    },

    'automate-spreadsheets': {
        title: 'Automate Spreadsheets—Keep Google Sheets & Excel Updated | Data Connector',
        metaDescription:
            'Keep Google Sheets and Excel updated automatically from Shopify, Stripe, Salesforce, and ads tools—no copy/paste, no weekly CSV imports.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Sheets open Monday already filled—not pasted.',
            headlineAccent: '',
            lead: 'Finance and ops open the same workbook path—rows landed overnight instead of copy/pasted from exports.',
            workflowPills: ['Stripe → Sheets', 'Salesforce → Excel', 'Meta Ads → Sheets'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Works with Google Sheets, Excel Online, and downloadable workbooks.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Paste exports into frozen template tabs',
                'Break formulas when someone inserts a row wrong',
                'Email “final_final_v3” files across the team',
            ],
            [
                'Tables append or overwrite on schedule',
                'Named ranges keep dashboards wired correctly',
                'Everyone trusts one canonical workbook',
            ],
            [
                { source: 'Stripe', dest: 'Google Sheets', outcome: 'Transactions and balances for cash tracking.' },
                { source: 'Salesforce', dest: 'Excel', outcome: 'Accounts and opportunities for regional rollups.' },
                { source: 'Shopify', dest: 'Sheets', outcome: 'SKU performance without manual pulls.' },
            ],
            [
                { src: sync2Png, alt: 'Selecting rows and fields for spreadsheet sync.', caption: 'Choose exactly which columns land in which tab.' },
                { src: homePng, alt: 'Dashboard showing spreadsheet sync jobs.', caption: 'Confirm each tab stayed current overnight.' },
            ],
            ['Transactions', 'CRM fields', 'Campaign metrics', 'Inventory counts', 'Budget lines', 'Pipeline stages', 'Weekly KPI tables'],
            'Google Sheets and Excel stay the front end—Data Connector keeps the back end fed.',
            [
                {
                    title: 'Open Sheets Monday—tabs already populated',
                    description: 'Skip the hour of cleanup before anyone builds a pivot.',
                },
                {
                    title: 'Stop breaking models when someone inserts a row wrong',
                    description: 'Structured refreshes land where named ranges expect them.',
                },
                {
                    title: 'One workbook path the whole team trusts',
                    description: 'No more ten "official" copies floating in Drive.',
                },
                {
                    title: 'Model scenarios with rows that are already here',
                    description: 'Spend time on what-if—not stitching sources by hand.',
                },
            ],
            ['stop-exporting-data', 'weekly-report-automation', 'crm-to-spreadsheet', 'sales-to-dashboard'],
            {
                headline: 'Ready to trust your spreadsheets again?',
                sub: 'Connect each source once—the rows you rely on stay fresh automatically.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Tab refresh', value: 'Auto' },
                        { label: 'Rows landed', value: '9,772' },
                        { label: 'Last sync', value: '2 min ago' },
                    ],
                    bullets: [
                        'Pick exactly which columns land in which tab—once.',
                        'Confirm overnight jobs so morning reviews start clean.',
                        'Keep Google Sheets or Excel as the front end your team already knows.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Paste exports into frozen template tabs',
                        'Break formulas when someone inserts a row mid-sheet',
                        'DM "use_final_final_v3.xlsx" across the team',
                        'Guess which copy leadership opened',
                    ],
                    after: [
                        'Tables overwrite or append on a schedule you control',
                        'Named ranges keep dashboards wired through refreshes',
                        'Everyone opens one canonical workbook path',
                        'You analyze instead of janitoring CSV fragments',
                    ],
                },
                pullQuote:
                    'The spreadsheet stays the hub—only the plumbing underneath finally matches how you work.',
            },
        ),
    },

    'orders-to-accounting': {
        title: 'Orders to Accounting Automation—From Storefront to Books | Data Connector',
        metaDescription:
            'Automate orders into QuickBooks and accounting tools from Shopify, WooCommerce, and Stripe. Fewer CSV imports and cleaner reconciliation.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Orders and payouts hit the ledger—without batch CSV uploads.',
            headlineAccent: '',
            lead: 'Channels feed accounting on your cadence—fewer settlement hunts and fewer “which export?” moments at close.',
            workflowPills: ['Shopify → QuickBooks', 'Stripe → QuickBooks', 'Amazon → Sheets'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Built for ops + finance teams juggling multiple sales channels.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Download order CSVs from each channel',
                'Manually batch settlements into the GL',
                'Lose hours matching payouts to deposits',
            ],
            [
                'Orders and payouts appear on predictable schedules',
                'Finance sees the same detail ops relies on',
                'Month-end closes with fewer surprises',
            ],
            [
                { source: 'Shopify', dest: 'QuickBooks', outcome: 'Sales receipts aligned with payouts.' },
                { source: 'Amazon', dest: 'QuickBooks', outcome: 'Marketplace settlements without paste jobs.' },
                { source: 'Stripe', dest: 'Sheets', outcome: 'Transaction detail for cash reviewers.' },
            ],
            [
                { src: sync2Png, alt: 'Choosing order and payout fields to sync.', caption: 'Map the fields finance needs—once.' },
                { src: homePng, alt: 'Monitoring accounting-related syncs.', caption: 'Catch issues before close—not after.' },
            ],
            ['Orders', 'Refunds', 'Fees', 'Tax lines', 'Payout batches', 'SKU revenue', 'Channel splits'],
            'Land operational truth in QuickBooks, Xero-friendly exports, Sheets, and databases for heavier ERP bridges.',
            [
                {
                    title: 'Close faster when orders hit the ledger nightly',
                    description: 'Numbers converge before your soft-close—not during an all-nighter.',
                },
                {
                    title: 'Show auditors transactions tied to real syncs',
                    description: 'Every line traces to a connection—not a forwarded CSV chain.',
                },
                {
                    title: 'Stop being ops\' human CSV courier',
                    description: 'Orders and payouts flow without you babysitting exports.',
                },
                {
                    title: 'Turn on a new channel without a new manual playbook',
                    description: 'Add the storefront—reuse the same mapping into accounting.',
                },
            ],
            ['sales-to-dashboard', 'sync-data-between-apps', 'stop-exporting-data', 'crm-to-spreadsheet'],
            {
                headline: 'Ready to close the loop between sales and books?',
                sub: 'Connect orders once—accounting stays aligned every day.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Payout batch', value: 'Synced' },
                        { label: 'Last sync', value: '9:02 PM' },
                        { label: 'Records', value: '3,441' },
                    ],
                    bullets: [
                        'Map order, refund, and fee fields once—finance sees the same detail ops uses.',
                        'Catch failed runs before close—not after someone spots a gap.',
                        'Land in QuickBooks, Sheets, or a warehouse your ERP bridge reads.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Download orders from every channel separately',
                        'Batch settlements into the GL by hand',
                        'Match payouts to deposits with detective work',
                        'Explain variances with inbox archaeology',
                    ],
                    after: [
                        'Orders and payouts appear on schedules you own',
                        'Finance sees the same line detail ops relies on',
                        'Month-end ties back to automated feeds',
                        'New channels plug into the same flow',
                    ],
                },
                pullQuote:
                    'Sales channels stop being side quests for finance—because the books ingest what already happened.',
            },
        ),
    },

    'sales-to-dashboard': {
        title: 'Sales to Dashboard Automation—Pipeline & Revenue Always Ready | Data Connector',
        metaDescription:
            'Automate sales reporting: sync Salesforce, HubSpot, and billing data into Sheets, Power BI, and Looker Studio so revenue reviews start with live numbers.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'Forecast decks open live—CRM + billing, not screenshots.',
            headlineAccent: '',
            lead: 'Pipeline, bookings, and billing hydrate before the room fills—no screenshot paste jobs for QBR week.',
            workflowPills: ['Salesforce → Power BI', 'HubSpot → Sheets', 'Stripe → Looker Studio'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Built for revenue, ops, and finance reviews.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Screenshot Salesforce for leadership decks',
                'Merge spreadsheets from each region',
                'Spend QBR prep stitching numbers',
            ],
            [
                'Dashboard tiles hydrate from live CRM + billing',
                'Regional slices stay comparable',
                'Forecast meetings start with current pipeline math',
            ],
            [
                { source: 'Salesforce', dest: 'Power BI', outcome: 'Rep and segment performance without exports.' },
                { source: 'HubSpot', dest: 'Google Sheets', outcome: 'Pipeline tabs for weekly forecast rituals.' },
                { source: 'Stripe', dest: 'Looker Studio', outcome: 'Recognized revenue beside funnel metrics.' },
            ],
            [
                { src: homePng, alt: 'Sales and revenue sync monitoring.', caption: 'See every feed powering the revenue story.' },
                { src: sync5Png, alt: 'Review sync affecting dashboards.', caption: 'Finalize schedules so dashboards refresh before QBRs.' },
            ],
            ['Pipeline stages', 'Win rates', 'ACV', 'Bookings', 'Churn flags', 'Quota attainment', 'Regional splits'],
            'Power Sheets, Excel, Power BI, Looker Studio, and databases—wherever leadership reviews revenue.',
            [
                {
                    title: 'Spend forecast prep on story—not spreadsheet surgery',
                    description: 'Pipeline math is already refreshed when the meeting starts.',
                },
                {
                    title: 'Put leadership on one model—not six regional tabs',
                    description: 'Everyone reads the same semantic layer tied to live CRM + billing.',
                },
                {
                    title: 'Stop RevOps heroics every quarter',
                    description: 'CRM and invoicing land where revenue reporting expects them.',
                },
                {
                    title: 'Coach reps with territories that updated this morning',
                    description: 'Managers see real coverage—not last week\'s export snapshot.',
                },
            ],
            ['crm-to-spreadsheet', 'automated-reporting', 'weekly-report-automation', 'orders-to-accounting'],
            {
                headline: 'Want revenue reviews to start with live data?',
                sub: 'Wire CRM and billing once—dashboards stay current for every forecast.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Pipeline rows', value: '76,220' },
                        { label: 'Last sync', value: '18 min ago' },
                        { label: 'Bookings feed', value: 'Healthy' },
                    ],
                    bullets: [
                        'See every feed powering the revenue story before a QBR.',
                        'Schedule refreshes so tiles hydrate before leadership walks in.',
                        'Blend CRM stages with billing facts in Sheets, Excel, or BI.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Screenshot Salesforce for exec decks',
                        'Merge regional spreadsheets that never match',
                        'Spend QBR week stitching numbers',
                        'Answer booking questions from stale tabs',
                    ],
                    after: [
                        'Dashboard tiles hydrate from live CRM + billing',
                        'Regions stay comparable on the same refresh cadence',
                        'Forecast meetings start with current pipeline math',
                        'Leadership points at one model everyone trusts',
                    ],
                },
                pullQuote:
                    'Revenue reviews start with today\'s pipeline—not whatever export someone grabbed Tuesday.',
            },
        ),
    },

    'crm-to-spreadsheet': {
        title: 'CRM to Spreadsheet—Sync Salesforce & HubSpot to Sheets or Excel | Data Connector',
        metaDescription:
            'Keep Salesforce and HubSpot data in Google Sheets or Excel automatically. Accounts, deals, and activities stay current for analysts without CSV exports.',
        hero: {
            eyebrow: 'Use case',
            headlineLine1: 'CRM lands in your spreadsheet—kill the weekly export dump.',
            headlineAccent: '',
            lead: 'Accounts, deals, and activities flow into the workbook your team already models—no more “export all deals.”',
            workflowPills: ['Salesforce → Sheets', 'HubSpot → Excel', 'Pipedrive → Sheets'],
            primaryCta: 'Set up your first sync',
            secondaryCta: 'Browse connectors',
            secondaryTo: '/connectors',
            microcopy: 'Pick objects and fields once—refine anytime without engineering.',
            trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
        },
        ...blocks(
            [
                'Export “all deals” CSVs that are too wide to open',
                'Paste into tabs someone else already broke',
                'Fight version control on shared drives',
            ],
            [
                'Named tables refresh with the fields modeling needs',
                'Ops and finance share one workbook version',
                'Analysts spend time on insight—not cleanup',
            ],
            [
                { source: 'Salesforce', dest: 'Google Sheets', outcome: 'Opportunities and accounts for territory planning.' },
                { source: 'HubSpot', dest: 'Excel', outcome: 'Lifecycle stages for lifecycle campaigns.' },
                { source: 'HubSpot', dest: 'Sheets', outcome: 'Marketing-qualified leads for SDR routing.' },
            ],
            [
                { src: sync2Png, alt: 'Selecting CRM objects and columns.', caption: 'Choose only the objects analysts actually model.' },
                { src: sync3Png, alt: 'Sending CRM data to a spreadsheet destination.', caption: 'Land rows where VLOOKUPs and pivots already live.' },
            ],
            ['Accounts', 'Contacts', 'Deals', 'Activities', 'Owners', 'Stages', 'Custom properties'],
            'Google Sheets, Excel, and downstream BI tools stay fed from the CRM fields you select.',
            [
                {
                    title: 'Give analysts columns that stay shaped like a table',
                    description: 'Structured refreshes beat exports so wide Excel warns before opening.',
                },
                {
                    title: 'Kill the Friday afternoon CRM dump ritual',
                    description: 'Rows land while you do real work—then modeling starts Monday morning.',
                },
                {
                    title: 'Run ops reviews off one tab everyone refreshed overnight',
                    description: 'No more "I used a different export than you" moments.',
                },
                {
                    title: 'Start scenarios with balances that match this morning',
                    description: 'What-if work begins from live pipeline—not Friday\'s CSV freeze.',
                },
            ],
            ['sales-to-dashboard', 'automate-spreadsheets', 'client-reporting', 'sync-data-between-apps'],
            {
                headline: 'Ready to stop exporting CRM CSVs every week?',
                sub: 'Connect your CRM once—the workbook your team trusts stays current.',
            },
            {
                spotlight: {
                    proof: [
                        { label: 'Objects synced', value: 'Opportunities' },
                        { label: 'Rows updated', value: '24,109' },
                        { label: 'Last sync', value: '3 min ago' },
                    ],
                    bullets: [
                        'Pick only the CRM objects analysts model—skip unused junk.',
                        'Send rows straight into the tabs your VLOOKUPs already hit.',
                        'Watch sync health so finance does not present stale AR data.',
                    ],
                },
                visualCompare: {
                    before: [
                        'Export "all deals" CSVs too wide to open',
                        'Paste into tabs someone already mangled',
                        'Fight version control on shared drives',
                        'Spend modeling time cleaning instead of forecasting',
                    ],
                    after: [
                        'Named tables refresh with the fields you chose',
                        'Ops and finance share one workbook version',
                        'Analysts open the same tab every morning',
                        'Insight work starts from rows that already landed',
                    ],
                },
                pullQuote:
                    'Your spreadsheet stays the workspace—CRM rows land there like they always should have.',
            },
        ),
    },
};

export const USE_CASE_SLUGS = Object.keys(USE_CASE_PAGE_CONFIG);
